<template>
  <el-dialog
    v-model="visible"
    class="legacy-owner-dialog"
    :class="`legacy-owner-dialog--${currentView}`"
    :width="currentView === 'select-region' ? '560px' : '520px'"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @closed="resetTransient"
  >
    <template #header>
      <svg v-if="currentView === 'success'" class="legacy-owner-dialog__success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m8 12 3 3 5-6"/></svg>
      <h2 class="legacy-owner-dialog__title">{{ title }}</h2>
    </template>

    <div class="legacy-owner-dialog__body" v-loading="busy">
      <p v-if="message" class="legacy-owner-dialog__message" :class="{ 'is-error': messageType === 'error' }">
        {{ message }}
      </p>

      <section v-if="currentView === 'unavailable'" class="legacy-owner-dialog__section">
        <p>{{ t('legacyOwner_unavailable_intro') }}</p>
        <p v-if="state?.status">{{ t('legacyOwner_status_line', { status: unavailableStatusText }) }}</p>
      </section>

      <section v-else-if="currentView === 'processing'" class="legacy-owner-dialog__section">
        <p>{{ t('legacyOwner_processing_description', { status: processingStatusText }) }}</p>
        <p v-if="processingWarning" class="legacy-owner-dialog__warning">
          {{ processingWarning }}
        </p>
      </section>

      <section v-else-if="currentView === 'success'" class="legacy-owner-dialog__section">
        <p>{{ t('legacyOwner_success_intro') }}</p>
        <p v-if="selectedMode">{{ t('legacyOwner_success_selected', { option: selectedOptionTitle }) }}</p>
        <p>{{ t('legacyOwner_success_refresh_tip') }}</p>
        <p v-if="selectedMode !== 'AI_ONLY'" class="legacy-owner-dialog__muted">{{ t('legacyOwner_success_migration_login_tip') }}</p>
      </section>

      <section v-else-if="currentView === 'bind-email'" class="legacy-owner-dialog__section legacy-owner-bind">
        <p>
          {{ migrationT('bind_intro') }}
        </p>
        <label class="legacy-owner-bind__field">
            <span>{{ migrationT('owner_email') }}</span>
              <el-input
                v-model="bindEmail"
                :readonly="Boolean(state?.ownerEmail)"
                :placeholder="migrationT('owner_email_placeholder')"
              />
        </label>
        <div class="legacy-owner-code">
          <el-input v-model="bindCode" maxlength="6" inputmode="numeric" :placeholder="migrationT('email_code_placeholder')" :aria-label="migrationT('email_code')" />
          <el-button :disabled="!ownerEmail || busy" @click="issueBindCode">
            {{ challengeId ? migrationT('resend_code') : migrationT('get_code') }}
          </el-button>
        </div>
        <el-input
          v-if="state?.needsPassword"
          v-model="newPassword"
          type="password"
          maxlength="20"
          show-password
          :placeholder="migrationT('password_placeholder')"
        />
        <el-input v-if="state?.needsPassword" v-model="repeatPassword" type="password" maxlength="20" show-password :placeholder="migrationT('repeat_password_placeholder')" />
        <p class="legacy-owner-dialog__muted">{{ migrationT('bind_code_tip') }}</p>
      </section>

      <section v-else-if="currentView === 'select-region'" class="legacy-owner-dialog__section">
        <p>
          {{ migrationT('select_region_intro') }}
        </p>
        <div class="legacy-owner-options">
          <button
            v-for="option in regionOptions"
            :key="option.value"
            type="button"
            class="legacy-owner-option"
            :class="{ 'is-selected': selectedMode === option.value }"
            :aria-pressed="selectedMode === option.value"
            :disabled="busy"
            @click="selectedMode = option.value"
          >
            <svg class="legacy-owner-option__radio" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.5"/><circle v-if="selectedMode === option.value" cx="10" cy="10" r="4" fill="currentColor"/></svg>
            <span class="legacy-owner-option__copy"><strong>{{ regionTitle(option.value) }}</strong><small>{{ regionSubtitle(option.value) }}</small></span>
          </button>
        </div>
      </section>

      <section v-else-if="currentView === 'single-confirm'" class="legacy-owner-dialog__section">
        <p>{{ selectedMode === 'AI_ONLY' ? migrationT('ai_confirm_intro') : migrationT('vip_confirm_intro') }}</p>
        <ol v-if="selectedMode === 'AI_ONLY'" class="legacy-owner-points">
          <li><span class="legacy-owner-points__number" aria-hidden="true">1</span><span>{{ migrationT('ai_confirm_point_1') }}</span></li>
          <li><span class="legacy-owner-points__number" aria-hidden="true">2</span><span>{{ migrationT('ai_confirm_point_2') }}</span></li>
          <li><span class="legacy-owner-points__number" aria-hidden="true">3</span><span>{{ migrationT('ai_confirm_point_3') }}</span></li>
        </ol>
        <ol v-else class="legacy-owner-points">
          <li><span class="legacy-owner-points__number" aria-hidden="true">1</span><span>{{ migrationT('vip_confirm_point_1') }}</span></li>
          <li><span class="legacy-owner-points__number" aria-hidden="true">2</span><span>{{ deadlineText ? migrationT('vip_confirm_point_2_with_deadline', { deadline: deadlineText }) : migrationT('vip_confirm_point_2') }}</span></li>
          <li><span class="legacy-owner-points__number" aria-hidden="true">3</span><span>{{ migrationT('vip_confirm_point_3') }}</span></li>
        </ol>
      </section>

      <section v-else-if="currentView === 'allocation'" class="legacy-owner-dialog__section">
        <div class="legacy-owner-quota-total">{{ migrationT('current_remaining_chars', { count: formatWords(totalWords) }) }}</div>
        <div class="legacy-owner-quota-grid">
          <label>
            <span>{{ migrationT('china_quota_pool') }}</span>
            <div class="legacy-owner-quota-input"><el-input :model-value="vipWan" :aria-label="migrationT('china_quota_pool_wan_aria')" inputmode="decimal" @update:model-value="setVipWan" /><small>{{ migrationT('wan_unit') }}</small></div>
          </label>
          <label>
            <span>{{ migrationT('international_quota_pool') }}</span>
            <div class="legacy-owner-quota-input"><el-input :model-value="aiWan" :aria-label="migrationT('international_quota_pool_wan_aria')" inputmode="decimal" @update:model-value="setAiWan" /><small>{{ migrationT('wan_unit') }}</small></div>
          </label>
        </div>
        <div class="legacy-owner-range">
          <input
            type="range"
            :aria-label="migrationT('range_china_quota_aria')"
            min="0"
            :max="rangeMax"
            step="1"
            :value="rangeMax === '0' ? '0' : vipQuota"
            :style="{ '--quota-progress': `${rangeMax === '0' ? 0 : Number(vipQuota) / Number(rangeMax) * 100}%` }"
            :disabled="totalWords === '0' || rangeMax === '0'"
            @input="onRangeInput"
          >
        </div>
        <p class="legacy-owner-quota-tip"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 11v6M12 7v1"/></svg>{{ migrationT('allocation_tip') }}</p>
        <p>{{ migrationT('allocation_independent_tip') }}</p>
        <p v-if="allocationError" class="legacy-owner-dialog__warning">{{ allocationError }}</p>
      </section>

      <section v-else-if="currentView === 'preflight'" class="legacy-owner-dialog__section">
        <div class="legacy-owner-space-summary">
          <section><div><strong>{{ migrationT('international_version_site') }}</strong><b>{{ migrationT('characters_value', { count: formatWords(selectedMode === 'CROSS_REGION' ? aiQuota : '0') }) }}</b></div><p>{{ selectedMode === 'CROSS_REGION' ? migrationT('existing_space_members_unchanged') : migrationT('history_kept_accessible') }}</p></section>
          <section>
            <div><strong>{{ migrationT('china_version_site') }}</strong><b>{{ migrationT('characters_value', { count: formatWords(selectedMode === 'CROSS_REGION' ? vipQuota : totalWords) }) }}</b></div>
            <p>{{ migrationT(preflight?.userExists ? 'use_owner_email_link_existing' : 'use_owner_email_create_account', { email: ownerEmail }) }}</p>
            <p v-if="preflight?.businessName || preflight?.targetBizTeamId">{{ migrationT('enterprise_label', { name: preflight.businessName || migrationT('enterprise_id', { id: preflight.targetBizTeamId }) }) }}</p>
          </section>
        </div>
        <p v-if="preflight?.reason" class="legacy-owner-dialog__muted">{{ preflight.reason }}</p>
        <p v-if="preflight?.action === 'MANUAL_REQUIRED'" class="legacy-owner-dialog__warning">
          {{ migrationT('manual_required_stop') }}
        </p>
        <el-checkbox v-if="preflight?.action === 'LEAVE_CONFIRM_REQUIRED'" v-model="confirmedLeave">
          {{ migrationT('confirm_leave_china_enterprise') }}
        </el-checkbox>
        <p class="legacy-owner-dialog__muted">{{ migrationT('preflight_consent_tip') }}</p>
      </section>
    </div>

    <template #footer>
      <div class="legacy-owner-dialog__footer">
        <el-button v-if="showBack" class="legacy-owner-dialog__secondary" :disabled="busy" @click="goBack">{{ migrationT('back_to_reselect') }}</el-button>
        <el-button v-if="currentView === 'processing'" :disabled="busy || !canCancelWaiting" @click="cancelWaiting">
          {{ t('legacyOwner_processing_cancel_waiting') }}
        </el-button>
        <el-button v-if="currentView === 'processing'" :loading="busy" @click="checkExecution">{{ t('legacyOwner_processing_check_status') }}</el-button>
        <el-button v-if="currentView === 'processing' && canRetryExecution" :disabled="busy" @click="retryExecution">{{ t('legacyOwner_processing_retry') }}</el-button>
        <el-button v-if="currentView === 'success' && selectedMode !== 'AI_ONLY' && activeMigrationNo" :loading="inviteCopying" @click="copySuccessInvite">{{ inviteCopied ? migrationT('copied') : migrationT('copy_invite_link') }}</el-button>
        <el-button v-if="currentView === 'success'" type="primary" @click="finish">{{ t('legacyOwner_success_done') }}</el-button>
        <el-button
          v-if="showPrimary"
          type="primary"
          class="legacy-owner-dialog__primary"
          :disabled="primaryDisabled"
          :loading="busy"
          @click="primaryAction"
        >
          {{ primaryText }}
        </el-button>
        <button v-if="currentView === 'select-region'" class="legacy-owner-dialog__defer" :disabled="busy" @click="deferDecision">{{ migrationT('handle_later') }}<span v-if="deadlineText">{{ migrationT('defer_deadline_tip', { deadline: deadlineText }) }}</span></button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import LegacyEnterpriseServer from '~/services/LegacyEnterpriseServer'
import LegacyEnterpriseOwnerServer from '~/services/LegacyEnterpriseServer/owner'
import type { LegacyTouchState } from '~/services/LegacyEnterpriseServer/types'
import type { LegacyExecuteRequest, LegacyExecution, LegacyPreflightResult, LegacyRegionMode } from '~/services/LegacyEnterpriseServer/ownerTypes'
import useUserStore from '~/store/modules/user'
import Toast from '~/utils/toast'
import { trustedEnterpriseWebUrl } from '~/utils/enterpriseWebLinks'
import {
  LEGACY_OWNER_REGION_OPTIONS,
  canExecuteLegacyOwnerMigration,
  defaultLegacyOwnerAllocation,
  forgetLegacyExecuteBody,
  freezeLegacyExecuteBody,
  formatLegacyOwnerWords,
  getRememberedLegacyExecuteBody,
  isLegacyExecutionCompleted,
  isLegacyExecutionPending,
  legacyOwnerStatusI18nKey,
  legacyOwnerRequestId,
  legacyOwnerTotalWords,
  quotaToWanInput,
  rememberLegacyExecuteBody,
  requiresLegacyPreflight,
  validateLegacyOwnerAllocation,
  wanInputToQuota,
} from '~/utils/legacyOwnerDialog'

const props = defineProps<{
  modelValue: boolean
  state: LegacyTouchState | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'refresh'): void
}>()

type View = 'unavailable' | 'select-region' | 'bind-email' | 'single-confirm' | 'allocation' | 'preflight' | 'processing' | 'success'

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})
const { t, locale } = useI18n()
const regionOptions = LEGACY_OWNER_REGION_OPTIONS
const busy = ref(false)
const selectedMode = ref<LegacyRegionMode>('AI_ONLY')
const currentView = ref<View>('select-region')
const activeMigrationNo = ref('')
const requestId = ref('')
const frozenExecuteBody = ref<Readonly<LegacyExecuteRequest> | null>(null)
const vipQuota = ref('0')
const preflight = ref<LegacyPreflightResult | null>(null)
const execution = ref<LegacyExecution | null>(null)
const confirmedLeave = ref(false)
const bindEmail = ref('')
const bindCode = ref('')
const newPassword = ref('')
const repeatPassword = ref('')
const inviteCopying = ref(false), inviteCopied = ref(false)
const challengeId = ref('')
const bindVerified = ref(false)
const message = ref('')
const messageType = ref<'info' | 'error'>('info')
const activeDraftKey = ref('')
let revision = 0
let disposed = false

const ownerEmail = computed(() => (props.state?.ownerEmail || bindEmail.value || preflight.value?.email || '').trim())
const totalWords = computed(() => {
  try { return legacyOwnerTotalWords(props.state) } catch { return '' }
})
const totalWordsError = computed(() => totalWords.value ? '' : migrationT('missing_total_words_refresh'))
const aiQuota = computed(() => {
  try { return totalWords.value ? validateLegacyOwnerAllocation(totalWords.value, vipQuota.value).aiQuota : '0' } catch { return '0' }
})
const rangeMax = computed(() => totalWords.value && BigInt(totalWords.value) <= BigInt(Number.MAX_SAFE_INTEGER) ? totalWords.value : '0')
const vipWan = computed(() => quotaToWanInput(vipQuota.value))
const aiWan = computed(() => quotaToWanInput(aiQuota.value))
const selectedOption = computed(() => regionOptions.find(option => option.value === selectedMode.value))
const deadlineText = computed(() => localDeadlineBefore(props.state?.deadline))
const allocationError = computed(() => {
  if (selectedMode.value !== 'CROSS_REGION') return ''
  if (totalWordsError.value) return totalWordsError.value
  try { validateLegacyOwnerAllocation(totalWords.value, vipQuota.value); return '' } catch (error: any) { return localizeOwnerText(error?.message || migrationT('allocation_invalid')) }
})
const canCancelWaiting = computed(() => ['WAITING_DRAIN', 'SOURCE_CHANGED'].includes(execution.value?.status || ''))
const canRetryExecution = computed(() => Boolean(frozenExecuteBody.value && !execution.value && currentView.value === 'processing' && !busy.value))
const unavailableStatusText = computed(() => t(legacyOwnerStatusI18nKey(props.state?.status)))
const processingStatusText = computed(() => t(legacyOwnerStatusI18nKey(execution.value?.status || props.state?.status || 'PENDING')))
const selectedOptionTitle = computed(() => regionTitle(selectedMode.value))
const processingWarning = computed(() => {
  if (execution.value?.manualRequired) return t('legacyOwner_processing_manual_required')
  if (execution.value?.failureReason || (execution.value && !isLegacyExecutionPending(execution.value))) return t('legacyOwner_processing_incomplete')
  return ''
})
const canExecute = computed(() => canExecuteLegacyOwnerMigration({
  mode: selectedMode.value,
  totalWords: totalWords.value || undefined,
  vipQuota: vipQuota.value,
  bindRequired: props.state?.bindRequired === true && !bindVerified.value,
  preflight: preflight.value,
  confirmedLeave: confirmedLeave.value,
}))
const primaryDisabled = computed(() => {
  if (busy.value) return true
  if (currentView.value === 'bind-email') return !canConfirmBind()
  if (currentView.value === 'allocation') return Boolean(allocationError.value)
  if (currentView.value === 'preflight') return preflight.value ? !canExecute.value : false
  return false
})
const showBack = computed(() => currentView.value === 'single-confirm')
const showPrimary = computed(() => ['select-region', 'single-confirm', 'allocation', 'preflight', 'bind-email'].includes(currentView.value))
const primaryText = computed(() => {
  if (currentView.value === 'select-region') return t('legacyOwner_primary_confirm')
  if (currentView.value === 'bind-email') return t('legacyOwner_primary_complete_email')
  if (currentView.value === 'preflight') return preflight.value ? t('legacyOwner_primary_accept_open') : t('legacyOwner_primary_open_vip')
  if (selectedMode.value === 'CROSS_REGION' && currentView.value === 'allocation') return t('legacyOwner_primary_confirm_allocation')
  if (selectedMode.value === 'AI_ONLY') return t('legacyOwner_primary_keep_ai')
  return t('legacyOwner_primary_migrate')
})
const title = computed(() => {
  const mapping: Record<View, string> = {
    unavailable: t('legacyOwner_title_unavailable'),
    'select-region': t('legacyOwner_title_select_region'),
    'bind-email': t('legacyOwner_title_bind_email'),
    'single-confirm': selectedMode.value === 'AI_ONLY' ? t('legacyOwner_title_keep_ai') : t('legacyOwner_title_migrate_vip'),
    allocation: t('legacyOwner_title_allocation'),
    preflight: t('legacyOwner_title_preflight'),
    processing: t('legacyOwner_title_processing'),
    success: t('legacyOwner_title_success'),
  }
  return mapping[currentView.value]
})

watch(() => props.modelValue, value => {
  if (value) initializeFromState()
}, { immediate: true })

watch(() => props.state?.migrationNo || '', next => {
  if (props.modelValue && next !== activeMigrationNo.value) initializeFromState()
})

function initializeFromState() {
  resetTransient()
  const state = props.state
  activeMigrationNo.value = state?.migrationNo || ''
  selectedMode.value = (state?.regionMode as LegacyRegionMode) || 'AI_ONLY'
  bindEmail.value = state?.ownerEmail || ''
  setDefaultQuota()
  activeDraftKey.value = executionDraftKey(activeMigrationNo.value)
  const remembered = getRememberedLegacyExecuteBody(activeDraftKey.value)
  if (remembered) {
    frozenExecuteBody.value = remembered
    requestId.value = remembered.requestId
    selectedMode.value = remembered.regionMode
    if (remembered.vipQuota) vipQuota.value = remembered.vipQuota
  }
  if (activeMigrationNo.value) {
    currentView.value = 'processing'
    setTimeout(() => { checkExecution({ allowEmptyToDecision: true }) }, 0)
    return
  }
  chooseDecisionView()
}

function chooseDecisionView() {
  const state = props.state
  if (isLegacyExecutionCompleted(null, state)) currentView.value = 'success'
  else if (state?.status === 'MIGRATING' || (state?.status === 'PENDING' && !state.canDecide)) currentView.value = 'processing'
  else if (!state?.owner || !state.canDecide) currentView.value = 'unavailable'
  else if (state.bindRequired && !bindVerified.value) currentView.value = 'bind-email'
  else currentView.value = 'select-region'
}

function resetTransient() {
  revision++
  busy.value = false
  preflight.value = null
  execution.value = null
  confirmedLeave.value = false
  bindEmail.value = ''
  bindCode.value = ''
  newPassword.value = ''
  repeatPassword.value = ''
  inviteCopying.value = false
  inviteCopied.value = false
  challengeId.value = ''
  bindVerified.value = false
  message.value = ''
  messageType.value = 'info'
  requestId.value = ''
  frozenExecuteBody.value = null
  activeDraftKey.value = ''
}

function migrationT(key: string, params?: Record<string, unknown>) {
  return t(`enterprise_ui_migration_${key}`, params || {})
}

const localeForIntl = computed(() => {
  const value = String(locale.value || 'zh')
  if (value === 'tw') return 'zh-TW'
  if (value === 'itb') return 'it'
  if (value === 'zh') return 'zh-CN'
  return value
})

function localDeadlineLabel(deadline?: string) {
  if (!deadline || !/(Z|[+-]\d{2}:\d{2})$/.test(deadline)) return ''
  const time = Date.parse(deadline)
  if (!Number.isFinite(time)) return ''
  return new Intl.DateTimeFormat(localeForIntl.value, {
    timeZone: 'Asia/Shanghai',
    month: 'short',
    day: 'numeric',
  }).format(new Date(time - 1))
}

function localDeadlineBefore(deadline?: string) {
  const date = localDeadlineLabel(deadline)
  return date ? migrationT('deadline_before', { date }) : ''
}

function localizeOwnerText(text: unknown) {
  const mapping: Record<string, string> = {
    '缺少企业字符总数': 'missing_total_words',
    '缺少企业字符总数，请刷新后重试': 'missing_total_words_refresh',
    '字符数必须使用精确的整数字符串': 'words_must_be_precise_integer',
    '字符数必须是非负整数': 'words_must_be_non_negative_integer',
    '字符数超出支持范围': 'words_out_of_range',
    '中国区字符不能超过总字符': 'china_quota_exceeds_total',
    '剩余字符大于 0 时，中国区和国际区都必须大于 0': 'both_quota_required',
    '请输入最多 4 位小数的万字符': 'wan_decimal_invalid',
    '分配无效': 'allocation_invalid',
  }
  const value = String(text || '')
  return mapping[value] ? migrationT(mapping[value]) : value
}

function setDefaultQuota() {
  vipQuota.value = defaultLegacyOwnerAllocation(totalWords.value || '0').vipQuota
}

function setMessage(text: string, type: 'info' | 'error' = 'info') {
  message.value = localizeOwnerText(text)
  messageType.value = type
  if (type === 'error') Toast(message.value, 'fail')
}

function formatWords(value: unknown) {
  return formatLegacyOwnerWords(value)
}

function regionTitle(mode: LegacyRegionMode | string) {
  const mapping: Record<string, string> = {
    VIP_ONLY: 'legacyOwner_region_vip_only',
    AI_ONLY: 'legacyOwner_region_ai_only',
    CROSS_REGION: 'legacyOwner_region_cross_region',
  }
  return t(mapping[mode] || 'legacyOwner_region_unknown')
}

function regionSubtitle(mode: LegacyRegionMode | string) {
  const mapping: Record<string, string> = {
    VIP_ONLY: 'region_vip_subtitle',
    AI_ONLY: 'region_ai_subtitle',
    CROSS_REGION: 'region_cross_subtitle',
  }
  return migrationT(mapping[mode] || 'region_unknown_subtitle')
}

function canConfirmBind() {
  if (!activeMigrationNo.value || !challengeId.value || !/^\d{6}$/.test(bindCode.value)) return false
  if (props.state?.needsPassword && (newPassword.value.length < 8 || newPassword.value.length > 20)) return false
  if (props.state?.needsPassword && newPassword.value !== repeatPassword.value) return false
  return true
}

async function runWithMigration(action: (migrationNo: string, isCurrent: () => boolean) => Promise<void>) {
  if (busy.value) return
  const currentRevision = revision
  const isAlive = () => !disposed && revision === currentRevision
  busy.value = true
  message.value = ''
  try {
    let migrationNo = activeMigrationNo.value
    if (!migrationNo) {
      const response = await LegacyEnterpriseOwnerServer.begin()
      if (!isAlive()) return
      if (response.code !== '1' || !response.data?.migrationNo) throw new Error(response.message || migrationT('create_context_failed'))
      migrationNo = response.data.migrationNo
      activeMigrationNo.value = migrationNo
      activeDraftKey.value = executionDraftKey(migrationNo)
      emit('refresh')
    }
    const isCurrent = () => isAlive() && activeMigrationNo.value === migrationNo
    if (!isCurrent()) return
    await action(migrationNo, isCurrent)
  } catch (error: any) {
    if (isAlive()) {
      setMessage(error?.message || migrationT('unknown_result_check_later'), 'error')
    }
  } finally {
    if (isAlive()) busy.value = false
  }
}

function primaryAction() {
  if (currentView.value === 'select-region') confirmRegion()
  else if (currentView.value === 'bind-email') confirmBind()
  else if (currentView.value === 'single-confirm') prepareExecution()
  else if (currentView.value === 'allocation') currentView.value = 'preflight'
  else if (currentView.value === 'preflight') preflight.value ? executeMigration() : loadPreflight()
}

function confirmRegion() {
  preflight.value = null
  confirmedLeave.value = false
  if (props.state?.bindRequired && !bindVerified.value) { currentView.value = 'bind-email'; return }
  currentView.value = selectedMode.value === 'CROSS_REGION' ? 'allocation' : 'single-confirm'
}

function goBack() {
  if (currentView.value === 'preflight') currentView.value = selectedMode.value === 'CROSS_REGION' ? 'allocation' : 'single-confirm'
  else currentView.value = 'select-region'
  preflight.value = null
  confirmedLeave.value = false
}

async function deferDecision() {
  await runWithMigration(async (migrationNo, isCurrent) => {
    const response = await LegacyEnterpriseServer.defer(migrationNo)
    if (response.code !== '1') throw new Error(response.message || migrationT('defer_failed'))
    if (!isCurrent()) return
    emit('refresh')
    visible.value = false
  })
}

async function issueBindCode() {
  await runWithMigration(async (migrationNo, isCurrent) => {
    if (!ownerEmail.value) throw new Error(migrationT('missing_owner_email_send_code'))
    const response = await LegacyEnterpriseOwnerServer.bindIssue({ migrationNo, email: ownerEmail.value })
    if (response.code !== '1' || !response.data?.challengeId) throw new Error(response.message || migrationT('send_code_failed'))
    if (!isCurrent()) return
    challengeId.value = response.data.challengeId
    setMessage(migrationT('code_sent'))
  })
}

async function confirmBind() {
  await runWithMigration(async (migrationNo, isCurrent) => {
    if (!canConfirmBind()) throw new Error(migrationT('fill_valid_code_password'))
    const response = await LegacyEnterpriseOwnerServer.bindConfirm({
      migrationNo,
      challengeId: challengeId.value,
      code: bindCode.value,
      newPassword: props.state?.needsPassword ? newPassword.value : undefined,
    })
    if (response.code !== '1') throw new Error(response.message || migrationT('email_verify_failed'))
    if (!isCurrent()) return
    bindVerified.value = true
    emit('refresh')
    currentView.value = 'select-region'
  })
}

function prepareExecution() {
  currentView.value = requiresLegacyPreflight(selectedMode.value) ? 'preflight' : 'preflight'
  if (!requiresLegacyPreflight(selectedMode.value)) executeMigration()
}

async function loadPreflight() {
  await runWithMigration(async (migrationNo, isCurrent) => {
    const response = await LegacyEnterpriseOwnerServer.preflight(migrationNo)
    if (response.code !== '1' || !response.data) throw new Error(response.message || migrationT('preflight_failed'))
    if (!isCurrent()) return
    preflight.value = response.data
    if (response.data.action === 'MANUAL_REQUIRED') {
      setMessage(response.data.reason || migrationT('manual_required_stopped'), 'error')
    }
  })
}

async function executeMigration() {
  await runWithMigration(async (migrationNo, isCurrent) => {
    const retryingFrozen = Boolean(frozenExecuteBody.value)
    if (!retryingFrozen && !canExecute.value) throw new Error(migrationT('complete_required_confirmations'))
    if (requestId.value) {
      const status = await LegacyEnterpriseOwnerServer.execution(migrationNo)
      if (!isCurrent()) return
      if (status.code !== '1') throw new Error(status.message || migrationT('status_query_failed_stopped'))
      if (status.code === '1' && status.data) {
        receiveExecution(status.data)
        return
      }
      if (!retryingFrozen) throw new Error(migrationT('missing_execution_record'))
    }
    if (!frozenExecuteBody.value) {
      if (!requestId.value) requestId.value = legacyOwnerRequestId()
      const allocation = selectedMode.value === 'CROSS_REGION'
        ? validateLegacyOwnerAllocation(totalWords.value, vipQuota.value)
        : undefined
      frozenExecuteBody.value = freezeLegacyExecuteBody({
        migrationNo,
        requestId: requestId.value,
        regionMode: selectedMode.value,
        vipQuota: allocation?.vipQuota,
        preflightToken: preflight.value?.preflightToken,
        confirmedLeave: confirmedLeave.value,
      })
      activeDraftKey.value = executionDraftKey(migrationNo)
      rememberLegacyExecuteBody(activeDraftKey.value, frozenExecuteBody.value)
    }
    currentView.value = 'processing'
    const response = await LegacyEnterpriseOwnerServer.execute(frozenExecuteBody.value)
    if (!isCurrent()) return
    if (response.code !== '1') {
      // 明确业务拒绝也先核验执行记录；只有查明确实未受理才允许重新预检。
      const status = await LegacyEnterpriseOwnerServer.execution(migrationNo)
      if (!isCurrent()) return
      if (status.code !== '1') throw new Error(migrationT('status_query_failed_retry_original'))
      if (status.data) { receiveExecution(status.data); return }
      requestId.value = ''
      frozenExecuteBody.value = null
      forgetLegacyExecuteBody(activeDraftKey.value)
      preflight.value = null
      confirmedLeave.value = false
      chooseDecisionView()
      throw new Error(response.message || migrationT('request_not_accepted'))
    }
    if (!response.data) throw new Error(migrationT('execution_unknown'))
    receiveExecution(response.data)
  })
}

async function checkExecution(options: { allowEmptyToDecision?: boolean } = {}) {
  const migrationNo = activeMigrationNo.value
  if (!migrationNo) return
  await runWithMigration(async (currentMigrationNo, isCurrent) => {
    if (currentMigrationNo !== migrationNo) return
    const response = await LegacyEnterpriseOwnerServer.execution(migrationNo)
    if (response.code !== '1') throw new Error(response.message || migrationT('status_query_failed'))
    if (!isCurrent()) return
    if (!response.data) {
      execution.value = null
      if (options.allowEmptyToDecision && !frozenExecuteBody.value) chooseDecisionView()
      else currentView.value = 'processing'
      return
    }
    receiveExecution(response.data)
  })
}

async function cancelWaiting() {
  const migrationNo = activeMigrationNo.value
  if (!migrationNo || !canCancelWaiting.value) return
  await runWithMigration(async (currentMigrationNo, isCurrent) => {
    if (currentMigrationNo !== migrationNo) return
    const response = await LegacyEnterpriseOwnerServer.cancelWaiting(migrationNo)
    if (response.code !== '1') throw new Error(response.message || migrationT('cancel_failed'))
    if (!isCurrent()) return
    requestId.value = ''
    frozenExecuteBody.value = null
    forgetLegacyExecuteBody(activeDraftKey.value)
    activeDraftKey.value = ''
    execution.value = null
    emit('refresh')
    currentView.value = 'select-region'
  })
}

function receiveExecution(next: LegacyExecution | null) {
  execution.value = next
  if (isLegacyExecutionCompleted(next, props.state)) {
    currentView.value = 'success'
    requestId.value = ''
    frozenExecuteBody.value = null
    forgetLegacyExecuteBody(activeDraftKey.value)
    activeDraftKey.value = ''
    refreshEnterpriseRights()
    emit('refresh')
  } else {
    currentView.value = 'processing'
    if (next?.manualRequired) setMessage(t('legacyOwner_processing_manual_required'), 'error')
    else if (next && !isLegacyExecutionPending(next)) setMessage(t('legacyOwner_processing_incomplete'), 'error')
  }
}

function retryExecution() {
  if (execution.value) { checkExecution(); return }
  if (!frozenExecuteBody.value) { checkExecution(); return }
  executeMigration()
}

function refreshEnterpriseRights() {
  const userStore = useUserStore()
  userStore.getUserInfo?.()
  userStore.getBizTeamInfo?.()
  userStore.getRoleRights?.()
}

function executionDraftKey(migrationNo?: string) {
  if (!migrationNo) return ''
  const userStore = useUserStore()
  const userInfo = userStore.userInfo as any
  const bizTeamInfo = userStore.bizTeamInfo as any
  return [
    userStore.token || '',
    userInfo?.id || userInfo?.userId || userInfo?.uid || '',
    userInfo?.bizTeamId || bizTeamInfo?.id || '',
    props.state?.ownerEmail || '',
    migrationNo,
  ].join('|')
}

function setVipQuota(value: unknown) {
  try {
    const allocation = validateLegacyOwnerAllocation(totalWords.value, value)
    vipQuota.value = allocation.vipQuota
  } catch (error: any) {
    vipQuota.value = String(value ?? '0')
    setMessage(error?.message || migrationT('allocation_invalid'), 'error')
  }
}

function onRangeInput(event: Event) {
  setVipQuota((event.target as HTMLInputElement).value)
}

function setVipWan(value: unknown) {
  try { setVipQuota(wanInputToQuota(value)) } catch (error: any) { setMessage(error?.message || migrationT('allocation_invalid'), 'error') }
}

function setAiWan(value: unknown) {
  try {
    const ai = wanInputToQuota(value)
    const nextVip = (BigInt(totalWords.value) - BigInt(ai)).toString()
    setVipQuota(nextVip.startsWith('-') ? '0' : nextVip)
  } catch (error: any) {
    setMessage(error?.message || migrationT('allocation_invalid'), 'error')
  }
}

async function copySuccessInvite() {
  if (inviteCopying.value || !activeMigrationNo.value) return
  const currentRevision = revision
  inviteCopying.value = true
  try {
    const result = await LegacyEnterpriseServer.invite(activeMigrationNo.value)
    if (disposed || currentRevision !== revision) return
    const url = result.data?.status === 'ACTIVE' ? trustedEnterpriseWebUrl(result.data.url) : ''
    if (result.code !== '1' || !url) throw new Error(migrationT('invite_unavailable_manage'))
    await navigator.clipboard.writeText(url)
    if (!disposed && currentRevision === revision) inviteCopied.value = true
  } catch (error: any) {
    if (!disposed && currentRevision === revision) setMessage(error?.message || migrationT('copy_failed_manage'), 'error')
  } finally {
    if (!disposed && currentRevision === revision) inviteCopying.value = false
  }
}

function finish() {
  emit('refresh')
  visible.value = false
}

onBeforeUnmount(() => {
  disposed = true
  revision++
})
</script>

<style lang="scss">
.el-dialog.legacy-owner-dialog {
  box-sizing: border-box;
  width: var(--el-dialog-width, 520px) !important;
  max-width: calc(100vw - 32px);
  padding: 28px !important;
  border-radius: 14px !important;
  max-height: calc(100dvh - 32px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  --el-color-primary: #2b7de9;
  box-shadow: 0 16px 44px #0a234d33;
}

.el-dialog.legacy-owner-dialog .el-dialog__header {
  height: auto !important;
  padding: 0 20px 14px 0 !important;
  margin-right: 0;
  flex-shrink: 0;
}

.legacy-owner-dialog .el-dialog__body {
  padding: 0 !important;
  word-break: normal;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  overflow-wrap: anywhere;
}

.legacy-owner-dialog .el-dialog__footer {
  padding: 0;
  flex-shrink: 0;
}
.legacy-owner-dialog .el-dialog__headerbtn { right: 18px; top: 24px; width: 24px; height: 24px; }
.legacy-owner-dialog p { margin: 0; }
.legacy-owner-dialog__muted { color: #8f959e; font-size: 12px; }
.legacy-owner-points { margin: 0; padding: 0; display: grid; gap: 14px; list-style: none; }
.legacy-owner-points li { display: flex; align-items: flex-start; gap: 10px; }
.legacy-owner-points__number { display: inline-flex; justify-content: center; align-items: center; flex: 0 0 20px; height: 20px; box-sizing: border-box; border: 1px solid #fde68a; border-radius: 50%; color: #d97706; background: #fff7e6; font-size: 11px; font-weight: 700; }
.legacy-owner-dialog--success .el-dialog__header { display: flex; flex-direction: column; align-items: center; gap: 10px; padding-right: 0 !important; }
.legacy-owner-dialog--success .legacy-owner-dialog__title { text-align: center; font-size: 19px; }
.legacy-owner-dialog__success-icon { width: 44px; height: 44px; color: #00b578; flex-shrink: 0; }

.legacy-owner-dialog__title {
  margin: 0;
  color: #1f2329;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
  text-align: left;
}

.legacy-owner-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0;
  color: #51565d;
  font-size: 13px;
  line-height: 1.6;
}

.legacy-owner-dialog__section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.legacy-owner-dialog__message,
.legacy-owner-dialog__warning {
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f9ff;
  color: #2b7de9;
}

.legacy-owner-dialog__message.is-error,
.legacy-owner-dialog__warning {
  background: #fff2f0;
  color: #c23b2a;
}

.legacy-owner-options {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.legacy-owner-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 13px 14px;
  border: 1px solid #d9dee5;
  border-radius: 10px;
  background: #fff;
  color: #1f2329;
  text-align: left;
  cursor: pointer;
  font: inherit;
  &__radio { flex: 0 0 16px; width: 16px; height: 16px; margin-top: 3px; color: #b7bec9; }
  &__copy { display: flex; flex-direction: column; gap: 4px; }
  strong { font-size: 14px; font-weight: 700; }

  small {
    color: #51565d;
    font-size: 12.5px;
  }

  &.is-selected {
    border-color: #2b7de9;
    box-shadow: inset 0 0 0 .5px #2b7de9;
    background: #f5f9ff;
    .legacy-owner-option__radio { color: #2b7de9; }
    strong { color: #2b7de9; }
  }
}

.legacy-owner-summary {
  display: grid;
  gap: 10px;
  margin: 0;

  div {
    display: grid;
    grid-template-columns: 96px 1fr;
    gap: 10px;
  }

  dt {
    color: #737a86;
  }

  dd {
    margin: 0;
    color: #1f2329;
    word-break: break-word;
  }
}

.legacy-owner-code {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 132px;
  gap: 10px;
}

.legacy-owner-range input {
  appearance: none;
  display: block;
  width: 100%;
  margin: 8px 0;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(to right, #2b7de9 var(--quota-progress), #c9ddfb var(--quota-progress));
  cursor: pointer;
  &::-webkit-slider-thumb { appearance: none; width: 20px; height: 20px; border: 1.5px solid #2b7de9; border-radius: 50%; background: #fff; }
  &::-moz-range-thumb { width: 18px; height: 18px; border: 1.5px solid #2b7de9; border-radius: 50%; background: #fff; }
  &:disabled { cursor: not-allowed; opacity: .5; }
  &:focus-visible { outline: 2px solid #2b7de9; outline-offset: 8px; }
}

.legacy-owner-quota-total { align-self: flex-start; padding: 8px 14px; border-radius: 8px; background: #f7f9fc; color: #51565d; font-size: 12.5px; font-weight: 600; }
.legacy-owner-quota-tip { display: flex; align-items: flex-start; gap: 6px; color: #8f959e; font-size: 12px; }
.legacy-owner-quota-tip svg { flex: 0 0 13px; width: 13px; height: 13px; margin-top: 3px; }
.legacy-owner-bind__field { display: flex; flex-direction: column; gap: 6px; }
.legacy-owner-dialog .el-input__wrapper { min-height: 38px; padding: 0 12px; border-radius: 8px; box-shadow: 0 0 0 1px #d9dee5 inset; }
.legacy-owner-dialog .el-input__inner { font-size: 13px; color: #1f2329; }
.legacy-owner-code .el-button { height: 38px; margin: 0; padding: 0 10px; border: 1px solid #2b7de9; color: #2b7de9; background: #fff; border-radius: 8px; font-size: 12.5px; }
.legacy-owner-code .el-button.is-disabled { opacity: .55; cursor: not-allowed; }

.legacy-owner-quota-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  label {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  span {
    color: #1f2329;
    font-weight: 600;
    font-size: 12.5px;
  }

  small {
    color: #8f959e;
    font-size: 12px;
  }
  .legacy-owner-quota-input { display: flex; align-items: center; gap: 6px; }
  .el-input__inner { text-align: center; font-size: 14px; font-weight: 700; }
}

.legacy-owner-space-summary { display: grid; gap: 14px; }
.legacy-owner-space-summary section { background: #f7f9fc; border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; gap: 5px; }
.legacy-owner-space-summary section > div { display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.legacy-owner-space-summary strong { color: #1f2329; font-size: 13.5px; font-weight: 700; }
.legacy-owner-space-summary b { color: #2b7de9; font-size: 13.5px; font-weight: 800; }
.legacy-owner-space-summary p { font-size: 12.5px; }

.legacy-owner-dialog__footer {
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding: 14px 0 0;
  background: #fff;

  .el-button {
    flex: 1;
    min-width: 0;
    width: auto;
    min-height: 40px;
    height: auto;
    padding: 9px 12px;
    margin-left: 0;
    border-radius: 8px;
    font-size: 13.5px;
    font-weight: 600;
    white-space: normal;
    line-height: 1.45;
    span { white-space: normal; overflow-wrap: anywhere; }
    &:not(.el-button--primary) { border-color: #d9dee5; color: #51565d; background: #fff; }
    &.is-disabled { cursor: not-allowed; opacity: .55; }
  }
}
.legacy-owner-dialog--select-region .legacy-owner-dialog__footer { flex-direction: column; }
.legacy-owner-dialog--processing .legacy-owner-dialog__footer { flex-wrap: wrap; }
.legacy-owner-dialog--processing .legacy-owner-dialog__footer .el-button { flex-basis: 40%; }
.legacy-owner-dialog__defer { border: 0; background: transparent; padding: 0; color: #8f959e; font-size: 12px; line-height: 1.6; cursor: pointer; }

.legacy-owner-dialog__primary {
  height: 40px;
  width: 100%;
  border-radius: 8px;
  background: #2b7de9;
  border-color: #2b7de9;
}

@media (max-width: 560px) {
  .el-dialog.legacy-owner-dialog { padding: 24px !important; }
  .legacy-owner-summary div { grid-template-columns: 80px minmax(0, 1fr); }
}
</style>
